<script setup>
import { ref, watch } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import SmartTitleInput from 'dashboard/modules/tasks/components/SmartTitleInput.vue';
import TaskDetailFields from 'dashboard/modules/kanban/components/shared/TaskDetailFields.vue';

const props = defineProps({
  item: { type: Object, required: true },
});
const emit = defineEmits(['update', 'close', 'remove']);

const showRemoveConfirm = ref(false);
const removeConfirmRef = ref(null);
const detailFieldsRef = ref(null);

function closeDropdowns() {
  detailFieldsRef.value?.closeDropdowns();
}

function requestRemove() {
  showRemoveConfirm.value = true;
}
function confirmRemove() {
  showRemoveConfirm.value = false;
  emit('remove');
}
function cancelRemove() {
  showRemoveConfirm.value = false;
}

onClickOutside(removeConfirmRef, () => {
  showRemoveConfirm.value = false;
});

const { t } = useI18n();

// ── local copy (usado só pelo título/smart input) ──────────────────
const local = ref({ ...props.item });
watch(
  () => props.item,
  v => {
    local.value = { ...v };
  },
  { deep: true }
);

function onFieldsUpdate(updated) {
  local.value = updated;
  emit('update', updated);
}

// ── título ───────────────────────────────────────────────────────
const titleDraft = ref(props.item.title ?? '');
const smartPending = ref({});

watch(
  () => props.item.title,
  v => {
    titleDraft.value = v ?? '';
  }
);

function onSmartPending(fields) {
  smartPending.value = fields;
}

function commitTitle() {
  const updated = { ...local.value };
  if (titleDraft.value.trim()) updated.title = titleDraft.value.trim();
  if (smartPending.value.priority)
    updated.priority = smartPending.value.priority;
  if (smartPending.value.assignee_id != null)
    updated.assignee_id = smartPending.value.assignee_id;
  if (smartPending.value.task_type)
    updated.task_type = smartPending.value.task_type;
  smartPending.value = {};
  local.value = updated;
  emit('update', { ...updated });
}
</script>

<template>
  <div
    class="flex flex-col h-full bg-n-background overflow-y-auto"
    @click="closeDropdowns"
  >
    <!-- Header -->
    <div class="flex items-center gap-2 px-4 pt-4 pb-2 shrink-0">
      <SmartTitleInput
        v-model="titleDraft"
        :placeholder="t('KANBAN.TASK_GROUPS.ITEM.TITLE_PLACEHOLDER')"
        class="flex-1 min-w-0 text-lg font-semibold text-n-slate-12 p-0"
        @update:pending-fields="onSmartPending"
        @blur="commitTitle"
        @submit="commitTitle"
      />
      <!-- Lixeira com confirmação -->
      <div ref="removeConfirmRef" class="relative flex-shrink-0">
        <button
          class="size-9 flex items-center justify-center rounded hover:bg-n-ruby-2 text-n-slate-9 hover:text-n-ruby-11 transition-colors"
          :class="showRemoveConfirm ? 'bg-n-ruby-2 text-n-ruby-11' : ''"
          @click.stop="requestRemove"
        >
          <span class="i-lucide-trash-2 size-4" />
        </button>
        <div
          v-if="showRemoveConfirm"
          class="absolute right-0 top-10 z-40 bg-n-background border border-n-weak rounded-xl shadow-xl py-2 px-3 flex flex-col gap-2 min-w-[148px]"
          @click.stop
        >
          <p class="text-xs text-n-slate-11 whitespace-nowrap">
            {{ t('KANBAN.TASK_GROUPS.ITEM.REMOVE_CONFIRM') }}
          </p>
          <div class="flex gap-1.5">
            <button
              class="flex-1 text-xs font-medium px-2 py-1 rounded-lg bg-n-ruby-9 text-white hover:bg-n-ruby-10 transition-colors"
              @click.stop="confirmRemove"
            >
              {{ t('KANBAN.TASK_GROUPS.ITEM.REMOVE_YES') }}
            </button>
            <button
              class="flex-1 text-xs font-medium px-2 py-1 rounded-lg bg-n-surface-2 text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              @click.stop="cancelRemove"
            >
              {{ t('KANBAN.TASK_GROUPS.ITEM.REMOVE_NO') }}
            </button>
          </div>
        </div>
      </div>
      <!-- Fechar -->
      <button
        class="size-9 flex-shrink-0 flex items-center justify-center rounded hover:bg-n-alpha-2 text-n-slate-9 hover:text-n-slate-12 transition-colors"
        @click="emit('close')"
      >
        <span class="i-lucide-x size-5" />
      </button>
    </div>

    <div class="px-4 pb-6">
      <TaskDetailFields
        ref="detailFieldsRef"
        :model-value="local"
        allow-card-leader
        @update:model-value="onFieldsUpdate"
      />
    </div>
  </div>
</template>
